import { Badge } from '../components/ui/Card';

interface Phase {
  q: string;
  title: string;
  bullets: string[];
  status: 'done' | 'now' | 'next' | 'future';
}

const phases: Phase[] = [
  {
    q: 'Q1',
    title: 'Foundation',
    status: 'done',
    bullets: [
      'AIML smart contract deployed on Polygon',
      'Initial presale opens',
      'WalletConnect + RainbowKit integration',
    ],
  },
  {
    q: 'Q2',
    title: 'Referral program',
    status: 'now',
    bullets: [
      'Referral links + click tracking',
      '20% USDT commissions, KYC-gated',
      'AIML bonus tiers (volume + milestone)',
      'Affiliate dashboard',
    ],
  },
  {
    q: 'Q3',
    title: 'Mobile & growth',
    status: 'next',
    bullets: [
      'Android app via Capacitor',
      'iOS PWA',
      'Weekly leaderboard prizes (5M AIML/week)',
      '4-week consistency streak bonuses',
    ],
  },
  {
    q: 'Q4',
    title: 'Token generation event',
    status: 'future',
    bullets: [
      'AIML token live on DEX',
      'Linear 6-month vesting from TGE for buys + bonuses',
      'CEX listing applications',
    ],
  },
];

export default function Roadmap() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div className="text-center mb-16">
        <div className="eyebrow mb-3">ROADMAP</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight">
          What's <span className="text-gradient">shipping</span>, and when
        </h1>
        <p className="mt-4 text-ink-600 max-w-xl mx-auto">
          Honest milestones. No vaporware. Every dot moves through Done → Now → Next.
        </p>
      </div>

      <div className="relative">
        {/* Vertical connector */}
        <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-magenta-500 via-violet-500 to-azure-500 opacity-40" />

        <div className="space-y-10">
          {phases.map((p, idx) => (
            <PhaseRow key={p.q} phase={p} index={idx} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PhaseRow({ phase, index }: { phase: Phase; index: number }) {
  const dot =
    phase.status === 'done'
      ? 'bg-success'
      : phase.status === 'now'
        ? 'bg-brand-gradient animate-pulse-glow'
        : phase.status === 'next'
          ? 'bg-violet-500'
          : 'bg-paper-300';

  const badgeKind =
    phase.status === 'done'
      ? 'success'
      : phase.status === 'now'
        ? 'info'
        : phase.status === 'next'
          ? 'neutral'
          : 'neutral';

  return (
    <div className="flex items-start gap-5 sm:gap-7 animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="relative shrink-0">
        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl ${dot} flex items-center justify-center font-display font-bold text-white text-lg sm:text-xl shadow-soft`}>
          {phase.q}
        </div>
      </div>
      <div className="card-base p-6 flex-1">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="font-display text-2xl font-semibold">{phase.title}</div>
          <Badge kind={badgeKind}>{phase.status}</Badge>
        </div>
        <ul className="space-y-1.5 text-sm text-ink-700">
          {phase.bullets.map((b) => (
            <li key={b} className="flex gap-2">
              <span className="text-violet-500 shrink-0">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
